import React, { useState, useEffect, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';
import Spinner from './Spinner';

interface ChatImageMessageProps {
  initialMediaUrl?: string;
  mediaId?: string;
  reschedule?: (mediaId?: string) => Promise<string | undefined>;
  altText?: string;
  className?: string;
  maxRetry?: number;
  setSrc?: (url?: string) => void;
}

const RETRY_DELAY_MS = 2_500;
const MAX_RETRY = 10;

const ImageMiniComponent: React.FC<ChatImageMessageProps> = ({
  initialMediaUrl,
  reschedule,
  mediaId,
  altText,
  className,
  maxRetry = MAX_RETRY,
  setSrc,
}) => {
  const [isLoading, setLoading] = useState(false);

  const [baseUrl, setBaseUrl] = useState<string | undefined>(initialMediaUrl);

  const [activeSrc, setActiveSrc] = useState<string | undefined>(
    () => `${initialMediaUrl}?preview=true`,
  );

  const [retryCount, setRetryCount] = useState(0);
  const [isUsingPreview, setIsUsingPreview] = useState(true);

  const buildUrl = (url: string, params: Record<string, string | number>) => {
    try {
      const urlObj = new URL(url);
      Object.entries(params).forEach(([key, value]) => {
        urlObj.searchParams.set(key, String(value));
      });
      return urlObj.toString();
    } catch (e) {
      const separator = url.includes('?') ? '&' : '?';
      const queryString = Object.entries(params)
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
      return `${url}${separator}${queryString}`;
    }
  };

  useEffect(() => {
    if (!mediaId) {
      setActiveSrc(undefined);
      return;
    }

    if (isUsingPreview) {
      const params: Record<string, string | number> = { preview: 'true' };

      if (retryCount > 0) {
        params.retryAttempt = retryCount;
      }

      setActiveSrc(buildUrl(baseUrl ?? '', params));
    } else {
      setActiveSrc(baseUrl);
    }
  }, [baseUrl, isUsingPreview, mediaId, retryCount]);

  const handleError = useCallback(async () => {
    if (!isUsingPreview) {
      setActiveSrc(undefined);
      return;
    }

    if (retryCount >= maxRetry) {
      setIsUsingPreview(false);
      setActiveSrc(baseUrl);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      if (reschedule) {
        const newUrl = await reschedule(mediaId);

        if (newUrl) {
          setBaseUrl(newUrl);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

      setRetryCount((prev) => prev + 1);
    } catch (error) {
      setRetryCount((prev) => prev + 1);
    } finally {
      // setLoading(false);
    }
  }, [baseUrl, isUsingPreview, maxRetry, mediaId, reschedule, retryCount]);

  if (isLoading) {
    return (
      <div
        className={twJoin(
          className,
          'bg-gray-200 flex relative animate-pulse border-none',
        )}
      >
        <Spinner className="mx-auto my-auto" />
        {activeSrc && (
          <img
            src={activeSrc}
            onError={handleError}
            onLoad={() => setLoading(false)}
            className="hidden"
            alt=""
          />
        )}
      </div>
    );
  }

  if (!activeSrc) {
    return (
      <div
        className={twJoin(
          className,
          'flex items-center justify-center text-xs text-gray-400 bg-gray-100',
        )}
      >
        Error
      </div>
    );
  }

  return (
    <img
      alt={altText || 'image'}
      src={activeSrc}
      onClick={() => setSrc && setSrc(baseUrl)}
      onError={handleError}
      className={twJoin(className, 'object-cover')}
    />
  );
};

export default ImageMiniComponent;
